"use client";

import { createStyleObject } from "@capsizecss/core";
import { type Glyph, parse } from "opentype.js";
import { Fragment, useEffect, useState } from "react";
import decompress from "woff2-encoder/decompress";
import Dropdown from "@/components/Dropdown";
import SectionHeading from "@/components/SectionHeading";
import { slugify } from "@/lib/utils";

interface CharacterSetProps {
	heading?: string;
	fonts: any;
}

interface GlyphsByCategory {
	Uppercase: Glyph[];
	Lowercase: Glyph[];
	Uppercase_Accented: Glyph[];
	Lowercase_Accented: Glyph[];
	Numbers: Glyph[];
	Superscript_And_Subscript: Glyph[];
	Fractions: Glyph[];
	Punctuation: Glyph[];
	Symbols: Glyph[];
	Mathematical: Glyph[];
	Ligatures: Glyph[];
	Accents: Glyph[];
	Alternative_Glyphs: Glyph[];
	Uncategorized: Glyph[];
}

function categorizeGlyph(glyph: Glyph): keyof GlyphsByCategory {
	const unicode = glyph.unicode ?? null;
	const name = glyph.name;
	const lowerName = name.toLowerCase();

	// First attempt categorization by Unicode (most reliable)
	if (unicode !== null) {
		// Basic Latin numbers
		if (unicode >= 0x30 && unicode <= 0x39) {
			return "Numbers";
		}

		// Basic Latin uppercase
		if (unicode >= 0x41 && unicode <= 0x5a) {
			return "Uppercase";
		}

		// Basic Latin lowercase
		if (unicode >= 0x61 && unicode <= 0x7a) {
			return "Lowercase";
		}

		// Latin-1 accented uppercase (À-Ö, Ø-Þ)
		if (
			(unicode >= 0x00c0 && unicode <= 0x00d6) || // À-Ö
			(unicode >= 0x00d8 && unicode <= 0x00de) // Ø-Þ
		) {
			return "Uppercase_Accented";
		}

		// Latin-1 accented lowercase (à-ö, ø-þ)
		if (
			(unicode >= 0x00e0 && unicode <= 0x00f6) || // à-ö
			(unicode >= 0x00f8 && unicode <= 0x00fe) // ø-þ
		) {
			return "Lowercase_Accented";
		}

		// Latin Extended-A: accented characters
		if (unicode >= 0x0100 && unicode <= 0x017f) {
			// Even codes are uppercase, odd codes are lowercase
			return unicode % 2 === 0 ? "Uppercase_Accented" : "Lowercase_Accented";
		}

		// Latin Extended-B: additional accented characters
		if (unicode >= 0x0180 && unicode <= 0x024f) {
			// This range is more complex, use name-based detection as fallback
			// but first check for known patterns
			if (
				(unicode >= 0x01a0 && unicode <= 0x01a6) || // Latin Extended-B specific
				(unicode >= 0x01cd && unicode <= 0x01db) || // Latin Extended-B specific
				(unicode >= 0x01de && unicode <= 0x01ee) || // Latin Extended-B specific
				(unicode >= 0x01f8 && unicode <= 0x021e) || // Latin Extended-B specific
				(unicode >= 0x0222 && unicode <= 0x0232) // Latin Extended-B specific
			) {
				return unicode % 2 === 0 ? "Uppercase_Accented" : "Lowercase_Accented";
			}
		}

		// Superscripts and subscripts
		if (
			(unicode >= 0x2070 && unicode <= 0x209f) || // Superscripts and Subscripts block
			unicode === 0x00b2 || // ²
			unicode === 0x00b3 || // ³
			unicode === 0x00b9 // ¹
		) {
			return "Superscript_And_Subscript";
		}

		// Fractions
		if (
			(unicode >= 0x00bc && unicode <= 0x00be) || // ¼, ½, ¾
			(unicode >= 0x2150 && unicode <= 0x215f) || // Fractions block
			unicode === 0x2044 // Fraction slash
		) {
			return "Fractions";
		}

		// Mathematical symbols - check before punctuation to catch basic math operators
		if (
			(unicode >= 0x2200 && unicode <= 0x22ff) || // Mathematical Operators
			(unicode >= 0x27c0 && unicode <= 0x27ef) || // Miscellaneous Mathematical Symbols-A
			(unicode >= 0x2980 && unicode <= 0x29ff) || // Miscellaneous Mathematical Symbols-B
			(unicode >= 0x2a00 && unicode <= 0x2aff) || // Supplemental Mathematical Operators
			unicode === 0x00b1 || // ±
			unicode === 0x00d7 || // ×
			unicode === 0x00f7 || // ÷
			// Basic mathematical operators that are in punctuation ranges
			unicode === 0x002b || // +
			unicode === 0x002d || // -
			unicode === 0x003d || // =
			unicode === 0x003c || // <
			unicode === 0x003e || // >
			unicode === 0x002a || // *
			unicode === 0x002f || // /
			unicode === 0x005e || // ^
			unicode === 0x007c || // |
			unicode === 0x007e // ~
		) {
			return "Mathematical";
		}

		// Combining diacritical marks (accents)
		if (
			(unicode >= 0x0300 && unicode <= 0x036f) || // Combining Diacritical Marks
			(unicode >= 0x1ab0 && unicode <= 0x1aff) || // Combining Diacritical Marks Extended
			(unicode >= 0x1dc0 && unicode <= 0x1dff) // Combining Diacritical Marks Supplement
		) {
			return "Accents";
		}

		// Currency and common symbols
		if (
			(unicode >= 0x20a0 && unicode <= 0x20cf) || // Currency Symbols
			unicode === 0x00a2 || // ¢
			unicode === 0x00a3 || // £
			unicode === 0x00a4 || // ¤
			unicode === 0x00a5 || // ¥
			unicode === 0x00a9 || // ©
			unicode === 0x00ae || // ®
			unicode === 0x0023 || // #
			unicode === 0x0024 || // $
			unicode === 0x0025 || // %
			unicode === 0x0040 || // @
			unicode === 0x00b0 || // °
			unicode === 0x00a7 || // §
			unicode === 0x00b6 || // ¶
			unicode === 0x2022 // •
		) {
			return "Symbols";
		}

		// Arrows
		if (
			(unicode >= 0x2190 && unicode <= 0x21ff) || // Arrows
			(unicode >= 0x27f0 && unicode <= 0x27bf) || // Supplemental Arrows-A
			(unicode >= 0x2900 && unicode <= 0x297f) // Supplemental Arrows-B
		) {
			return "Symbols";
		}

		// Geometric shapes
		if (
			(unicode >= 0x25a0 && unicode <= 0x25ff) || // Geometric Shapes
			(unicode >= 0x2b00 && unicode <= 0x2bff) // Miscellaneous Symbols and Arrows
		) {
			return "Symbols";
		}

		// Punctuation - exclude mathematical symbols and other symbols that were already caught
		if (
			(unicode >= 0x0021 &&
				unicode <= 0x002f &&
				![0x0023, 0x0024, 0x0025, 0x002a, 0x002b, 0x002d, 0x002f].includes(
					unicode,
				)) || // ! " & ' ( ) , . (exclude #, $, %, *, +, -, /)
			(unicode >= 0x003a &&
				unicode <= 0x0040 &&
				![0x003c, 0x003d, 0x003e, 0x0040].includes(unicode)) || // : ; ? (exclude < = > @)
			(unicode >= 0x005b && unicode <= 0x0060 && ![0x005e].includes(unicode)) || // [ \ ] _ ` (exclude ^)
			(unicode >= 0x007b &&
				unicode <= 0x007e &&
				![0x007c, 0x007e].includes(unicode)) || // { } (exclude | ~)
			(unicode >= 0x2000 && unicode <= 0x206f) || // General Punctuation
			(unicode >= 0x2e00 && unicode <= 0x2e7f) || // Supplemental Punctuation
			(unicode >= 0x00a1 &&
				unicode <= 0x00bf &&
				![
					0x00a2, 0x00a3, 0x00a4, 0x00a5, 0x00a7, 0x00a9, 0x00aa, 0x00ae,
					0x00b0, 0x00b2, 0x00b3, 0x00b6, 0x00b9, 0x00ba, 0x00bc, 0x00bd,
					0x00be,
				].includes(unicode)) // Latin-1 punctuation (exclude ¢, £, ¤, ¥, §, ©, ®, °, ², ³, ¶, ¹, ¼, ½, ¾)
		) {
			return "Punctuation";
		}

		// If Unicode is present but not categorized by the specific checks above,
		// consider it a Symbol
		return "Symbols";
	}

	// If no Unicode, categorize by name (less reliable but necessary for some fonts)

	// Ligatures - be more specific
	if (
		lowerName.includes("ligature") ||
		lowerName.includes("liga") ||
		lowerName.includes("_") ||
		// Common ligature names
		["fi", "fl", "ffi", "ffl", "ft", "st", "ct", "sp", "ss", "tt"].includes(
			lowerName,
		) ||
		// Font-specific ligature patterns
		(/^[a-z]{2,4}$/.test(lowerName) &&
			lowerName.length > 1 &&
			!lowerName.includes("comb")) ||
		// Common ligature prefixes
		/^f_[a-z]/.test(lowerName) ||
		/^ff[a-z]?$/.test(lowerName)
	) {
		return "Ligatures";
	}

	// Numbers by name - be more specific
	if (
		[
			"zero",
			"one",
			"two",
			"three",
			"four",
			"five",
			"six",
			"seven",
			"eight",
			"nine",
		].includes(lowerName) ||
		/^[0-9]$/.test(lowerName) ||
		/^digit[0-9]$/.test(lowerName) ||
		/^num[0-9]$/.test(lowerName)
	) {
		return "Numbers";
	}

	// Basic letters by name
	if (/^[a-z]$/.test(lowerName)) {
		// Check if it's actually a letter name
		if (name === name.toUpperCase()) {
			return "Uppercase";
		}
		if (name === name.toLowerCase()) {
			return "Lowercase";
		}
	}

	// Accented characters by name - be more specific
	const accentKeywords = [
		"acute",
		"grave",
		"circumflex",
		"tilde",
		"dieresis",
		"umlaut",
		"cedilla",
		"ring",
		"macron",
		"breve",
		"caron",
		"ogonek",
		"hook",
		"dot",
		"stroke",
		"slash",
		"bar",
	];

	for (const accent of accentKeywords) {
		if (lowerName.includes(accent)) {
			// Check if it's a combining mark vs a letter with accent
			if (lowerName.includes("comb") || lowerName.includes("combining")) {
				return "Accents";
			}

			// Check if the first letter indicates case
			const firstChar = name.charAt(0);
			if (
				firstChar === firstChar.toUpperCase() &&
				firstChar !== firstChar.toLowerCase()
			) {
				return "Uppercase_Accented";
			}
			if (
				firstChar === firstChar.toLowerCase() &&
				firstChar !== firstChar.toUpperCase()
			) {
				return "Lowercase_Accented";
			}
			// If we can't determine case, default to lowercase accented
			return "Lowercase_Accented";
		}
	}

	// Standalone combining marks
	if (
		lowerName.endsWith("comb") ||
		lowerName.includes("combining") ||
		(lowerName.includes("accent") && !lowerName.includes("letter")) ||
		lowerName.includes("diacritic")
	) {
		return "Accents";
	}

	// Superscript/subscript by name
	if (
		lowerName.includes("superior") ||
		lowerName.includes("superscript") ||
		lowerName.includes("subscript") ||
		lowerName.includes("inferior") ||
		lowerName.includes("super") ||
		lowerName.includes("sub")
	) {
		return "Superscript_And_Subscript";
	}

	// Fractions by name
	if (
		lowerName.includes("fraction") ||
		lowerName.includes("numerator") ||
		lowerName.includes("denominator") ||
		/^[0-9]+\/[0-9]+$/.test(lowerName) ||
		[
			"onehalf",
			"onequarter",
			"threequarters",
			"onethird",
			"twothirds",
			"oneeighth",
			"threeeighths",
			"fiveeighths",
			"seveneighths",
		].includes(lowerName)
	) {
		return "Fractions";
	}

	// Mathematical by name - be more comprehensive
	const mathKeywords = [
		"plus",
		"minus",
		"multiply",
		"divide",
		"equal",
		"greater",
		"less",
		"integral",
		"product",
		"summation",
		"radical",
		"infinity",
		"partial",
		"nabla",
		"increment",
		"gradient",
		"delta",
		"sigma",
		"pi",
		"theta",
		"alpha",
		"beta",
		"gamma",
		"lambda",
		"mu",
		"omega",
		"phi",
		"psi",
		"epsilon",
		"zeta",
		"eta",
		"iota",
		"kappa",
		"nu",
		"xi",
		"rho",
		"tau",
		"upsilon",
		"chi",
		"omega",
		"degree",
		"prime",
		"doubleprime",
	];

	for (const keyword of mathKeywords) {
		if (lowerName.includes(keyword)) {
			return "Mathematical";
		}
	}

	// Punctuation by name - be more comprehensive
	const punctKeywords = [
		"period",
		"comma",
		"colon",
		"semicolon",
		"exclamation",
		"question",
		"quotation",
		"quote",
		"apostrophe",
		"bracket",
		"parenthesis",
		"hyphen",
		"dash",
		"bullet",
		"ellipsis",
		"slash",
		"backslash",
		"pipe",
		"ampersand",
		"at",
		"hash",
		"dollar",
		"percent",
		"caret",
		"underscore",
		"tilde",
		"brace",
		"square",
		"angle",
		"bar",
		"dot",
		"space",
		"tab",
	];

	for (const keyword of punctKeywords) {
		if (lowerName.includes(keyword)) {
			return "Punctuation";
		}
	}

	// Currency and symbols by name
	const symbolKeywords = [
		"dollar",
		"cent",
		"pound",
		"euro",
		"yen",
		"copyright",
		"registered",
		"trademark",
		"arrow",
		"arrowup",
		"arrowdown",
		"arrowleft",
		"arrowright",
		"triangle",
		"circle",
		"square",
		"diamond",
		"star",
		"heart",
		"check",
		"cross",
		"plusminus",
		"infinity",
		"emptyset",
		"element",
		"notelement",
	];

	for (const keyword of symbolKeywords) {
		if (lowerName.includes(keyword)) {
			return "Symbols";
		}
	}

	// Fractions by name
	if (lowerName.includes(".ss")) {
		return "Alternative_Glyphs";
	}

	// If we can't categorize it with any method
	return "Uncategorized";
}

export default function CharacterSet({ heading, fonts }: CharacterSetProps) {
	const slug = slugify(heading);
	const [isLoading, setIsLoading] = useState(true);
	const [loadedFonts, setLoadedFonts] = useState(null);
	const [activeFontIndex, setActiveFontIndex] = useState(0);
	const [activeFont, setActiveFont] = useState(null);
	const [activeGlyph, setActiveGlyph] = useState(null);
	const [clickedGlyph, setClickedGlyph] = useState(null);
	console.log("loadedFonts:", loadedFonts);

	// useEffect(() => console.log('loaded font data:', loadedFonts[activeFontIndex]), [loadedFonts, activeFontIndex])

	useEffect(() => {
		let _fonts = []; //[...fonts]
		fonts.forEach((font: any, index: number) => {
			const url = font?.file?.asset?.url;
			const urlParts = url?.split("/");
			const filename = urlParts[urlParts.length - 1];
			// const extension = filename?.split('.').pop()

			function doData(data: any) {
				const categorizedGlyphs: GlyphsByCategory = {
					Uppercase: [],
					Lowercase: [],
					Uppercase_Accented: [],
					Lowercase_Accented: [],
					Numbers: [],
					Superscript_And_Subscript: [],
					Fractions: [],
					Punctuation: [],
					Symbols: [],
					Mathematical: [],
					Ligatures: [],
					Accents: [],
					Alternative_Glyphs: [],
					Uncategorized: [],
				};

				const glyphs = data?.glyphs?.glyphs;

				if (glyphs) {
					for (const key in glyphs) {
						if (glyphs.hasOwnProperty(key)) {
							const glyph = glyphs[key];
							const category = categorizeGlyph(glyph);
							categorizedGlyphs[category].push({
								...glyph,
								category,
							});
						}
					}
				}

				const metrics = {
					capHeight: data?.tables?.os2?.sCapHeight,
					xHeight: data?.tables?.os2?.sxHeight,
					ascent: data?.ascender || data?.tables?.os2?.sTypoAscender,
					descent: data?.descender || data?.tables?.os2?.sTypoDescender,
					lineGap: 0,
					unitsPerEm: data?.unitsPerEm || 1000,
				};

				const initialCapHeight = 100;

				const capsizeStyles = createStyleObject({
					capHeight: initialCapHeight,
					lineGap: 0,
					fontMetrics: metrics,
				});

				const fontScale =
					+capsizeStyles?.fontSize?.replace("px", "") / initialCapHeight; // Workout scale of fontSize to cap height

				const relativeMetrics = {
					capHeightRelative: (data?.tables?.os2?.sCapHeight * fontScale) / 1000,
					xHeightRelative: (data?.tables?.os2?.sxHeight * fontScale) / 1000,
					ascentRelative: (data?.tables?.os2?.sTypoAscender * fontScale) / 1000,
					descentRelative:
						(data?.tables?.os2?.sTypoDescender * fontScale) / 1000,
				};

				const initialGlyph = categorizedGlyphs?.Uppercase?.find(
					(glyph: Glyph) => glyph.unicodes?.[0] === 65,
				);

				setActiveGlyph(initialGlyph);
				setClickedGlyph(initialGlyph);

				_fonts.push({
					// ...font,
					filename,
					name: font?.name,
					weight: font?.weight,
					style: font?.style,
					categorizedGlyphs: categorizedGlyphs,
					metrics: {
						...metrics,
						...relativeMetrics,
					},
					capsizeStyles: {
						...capsizeStyles,
						fontScale,
						lineHeight: initialCapHeight / fontScale / 100,
					},
				});

				if (index === fonts.length - 1) {
					setLoadedFonts(_fonts);
				}
			}

			async function loadFont(filename: string) {
				// const arrayBuffer = await fetch(url).then(res => res.arrayBuffer())
				const arrayBuffer = await fetch(`/api/fonts/${filename}`).then((res) =>
					res.arrayBuffer(),
				);

				console.log(`${process.env.NEXT_PUBLIC_DOMAIN}/api/fonts/${filename}`);

				// If the loaded font file is a woff2 file, decompress it, otherwise, just parse it.
				let buffer: any = arrayBuffer;

				if (filename?.endsWith(".woff2")) {
					try {
						const decompressed = await decompress(arrayBuffer);
						buffer = decompressed.buffer;
					} catch (error) {
						// console.error('Error decompressing font:', error)
					}
				}

				let data = null;
				try {
					data = parse(buffer);
				} catch (error) {
					// console.error('Error parsing font:', error)
				}

				if (data) {
					doData(data);
				} else {
					// console.error('Error loading font data:', url)
				}
			}

			loadFont(filename);
		});
	}, [fonts]);

	useEffect(() => {
		if (!loadedFonts || loadedFonts.length === 0) return;
		// console.log('loadedFonts:', loadedFonts)
		setIsLoading(false);
	}, [loadedFonts]);

	useEffect(() => {
		if (isLoading) return;
		setActiveFont(loadedFonts[activeFontIndex]);
	}, [isLoading, activeFontIndex, loadedFonts]);

	const renderGlyphs = (category: keyof GlyphsByCategory) => {
		if (isLoading || !loadedFonts || loadedFonts.length === 0) return null;

		if (
			activeFont?.categorizedGlyphs?.[category]?.filter(
				(glyph: Glyph) => !!glyph.unicodes?.[0],
			)?.length < 4
		)
			return null;

		if (
			activeFont?.categorizedGlyphs?.[category]?.filter(
				(glyph: any) =>
					glyph.unicodes?.[0] &&
					![
						"nonmarkingreturn",
						"nonspacingmark",
						"spacingmark",
						"combiningchar",
						"space",
						"CR",
					].includes(glyph.name),
			)?.length < 4
		)
			return <div>No glyphs found</div>;

		return (
			<div className={`category-${category}`}>
				<h3 className="font-500 text-sm leading-100 mb-4">
					{category?.replaceAll("_", " ")?.replaceAll("And", "&")}
				</h3>
				<ul className="grid grid-cols-6 md:grid-cols-10 gap-1">
					{activeFont?.categorizedGlyphs?.[category]?.map((glyph, index) => {
						if (
							!glyph.unicodes?.[0] ||
							[
								"nonmarkingreturn",
								"nonspacingmark",
								"spacingmark",
								"combiningchar",
								"space",
								"CR",
								"nonspacingmark",
							].includes(glyph.name) ||
							glyph.name.startsWith("uni")
						)
							return null;
						// if (glyph.name.includes("uni0")) return null

						return (
							<li key={`${glyph?.name}-${index}`}>
								<button
									onClick={() => {
										setActiveGlyph(glyph);
										setClickedGlyph(glyph);
									}}
									onMouseEnter={() => setActiveGlyph(glyph)}
									onMouseLeave={() => setActiveGlyph(clickedGlyph)}
									className={`w-full aspect-square grid place-items-center rounded-[4px] group text-xl sm:text-2xl ${clickedGlyph?.index === glyph?.index ? "bg-black dark:bg-white text-white dark:text-black" : "bg-white dark:bg-black text-black dark:text-white border border-grey dark:border-grey-dark hover:border-black dark:hover:border-white hover:scale-[0.95]xxx transition-all"} character-tester-${slug}-${slugify(activeFont?.name)}`}
									data-glyph-unicodes={glyph?.unicodes?.[0]}
									data-glyph-name={glyph?.name}
									type="button"
								>
									<span
										className="md:group-hover:scale-[1.2] lg:group-hover:scale-[1.4] transition-all"
										dangerouslySetInnerHTML={{
											__html: `&#${glyph.unicodes?.[0]}`,
										}}
									/>
								</button>
							</li>
						);
					})}
				</ul>
			</div>
		);
	};

	if (isLoading || !loadedFonts || loadedFonts.length === 0) return null;

	// if (!activeFont?.categorizedGlyphs || Object.keys(activeFont?.categorizedGlyphs)?.length === 0) return null

	if (!activeFont?.categorizedGlyphs) return null;

	return (
		<div className={`relative`}>
			<div className="sticky z-200 top-0 sm:top-offset-header pt-offset-header sm:pt-4 sm:flex items-center justify-between gap-4 sm:gap-6 md:gap-8 bg-white dark:bg-black sm:bg-transparent sm:dark:bg-transparent">
				<div className="col-span-full sm:col-span-1 mb-2 md:mb-0 pt-4 sm:pt-0">
					<SectionHeading heading={heading || "Character Set"} />
				</div>
				<div className="col-span-full sm:col-span-1">
					<Dropdown
						options={loadedFonts?.map((font: any, index: number) => ({
							label: font?.name,
							value: index,
							defaultValue: 0 === index,
						}))}
						onChange={setActiveFontIndex}
					/>
				</div>
			</div>

			<div
				className={`relative z-100 sm:grid grid-cols-2 items-start gap-4 sm:gap-6 md:gap-8 bg-white dark:bg-black sm:bg-transparent`}
			>
				<div className="w-full col-span-1 overflow-clip sticky z-200 top-[calc(var(--offset-header)+74px)] sm:top-[calc(var(--offset-header)+47px)] xl:top-[calc(var(--offset-header)+49px)] overflow-x-clip h-auto xl:h-[calc(100dvh-var(--offset-header)-3rem)] flex flex-col sm:pb-6 md:pb-8 bg-white dark:bg-black sm:bg-transparent">
					<div className="relative z-100 flex-none font-500 text-sm leading-100 mb-4 flex items-center gap-2 py-4 bg-white dark:bg-black">
						<span>
							{activeGlyph?.category
								? `${activeGlyph?.category?.replaceAll("_", " ")?.replaceAll("And", "&")} / ${activeGlyph?.name}`
								: activeGlyph?.name}
						</span>
						<span className="text-grey dark:text-grey-dark">
							U+{activeGlyph?.unicode?.toString()?.padStart(4, "0")}
						</span>
					</div>

					<div className="flex-auto relative w-full h-[75vw] sm:h-[45vw] bg-grey-dark/5 flex items-center justify-center py-14 xl:py-8 pointer-events-none">
						{activeGlyph && (
							<div
								className={`relative w-full flex-none flex items-center justify-center`}
							>
								{activeFont?.metrics?.ascent > 0 && (
									<div
										id="ascender"
										className="absolute bottom-full flex justify-between w-full border-grey dark:border-grey-dark text-grey dark:text-grey-dark border-b border-dashed text-[9px] md:text-xs tracking-20"
										style={{
											bottom: activeFont?.metrics?.ascentRelative
												? `${activeFont?.metrics?.ascentRelative * 100}%`
												: "0%",
										}}
									>
										<span className="uppercase">Ascender</span>
										<span className="value">{activeFont?.metrics?.ascent}</span>
									</div>
								)}

								{Math.abs(
									activeFont?.metrics?.ascent - activeFont?.metrics?.capHeight,
								) > 20 && (
									<div
										id="cap-height"
										className="absolute flex justify-between w-full border-grey dark:border-grey-dark text-grey dark:text-grey-dark border-b border-dashed text-[9px] md:text-xs tracking-20"
										style={{
											bottom: activeFont?.metrics?.capHeightRelative
												? `${activeFont?.metrics?.capHeightRelative * 100}%`
												: "0%",
										}}
									>
										<span className="uppercase">Cap height</span>
										<span className="value">
											{activeFont?.metrics?.capHeight}
										</span>
									</div>
								)}

								{activeFont?.metrics?.xHeight > 0 && (
									<div
										id="x-height"
										className="absolute flex justify-between w-full border-grey dark:border-grey-dark text-grey dark:text-grey-dark border-b border-dashed text-[9px] md:text-xs tracking-20"
										style={{
											bottom: activeFont?.metrics?.xHeightRelative
												? `${activeFont?.metrics?.xHeightRelative * 100}%`
												: "0%",
										}}
									>
										<span className="uppercase">X height</span>
										<span className="value">
											{activeFont?.metrics?.xHeight}
										</span>
									</div>
								)}

								<div
									id="baseline"
									className="absolute flex justify-between w-full border-grey dark:border-grey-dark text-grey dark:text-grey-dark border-b border-dashed text-[9px] md:text-xs tracking-20"
									style={{
										bottom: 0,
									}}
								>
									<span className="uppercase">Baseline</span>
									<span className="value">0</span>
								</div>

								{activeFont?.metrics?.descent < 0 && (
									<div
										id="descender"
										className="absolute flex justify-between w-full border-grey dark:border-grey-dark text-grey dark:text-grey-dark border-b border-dashed text-[9px] md:text-xs tracking-20"
										style={{
											bottom: activeFont?.metrics?.descentRelative
												? `${activeFont?.metrics?.descentRelative * 100}%`
												: "0%",
										}}
									>
										<span className="uppercase">Descender</span>
										<span className="value">
											{activeFont?.metrics?.descent}
										</span>
									</div>
								)}

								<div
									className={`text-center character-tester-${slug}-${slugify(activeFont?.name)} character-tester-${slug}-${slugify(activeFont?.name)}-glyph text-[25vh] md:text-[50vh] leading-[normal]`}
									style={{
										lineHeight: activeFont?.capsizeStyles?.lineHeight,
									}}
								>
									<span
										className="relative"
										dangerouslySetInnerHTML={{
											__html: activeGlyph
												? `&#${activeGlyph?.unicodes?.[0]}`
												: "",
										}}
									/>
								</div>
							</div>
						)}
					</div>
				</div>

				<div className="relative z-100 col-span-1 pt-4 grid grid-cols-1 gap-y-8 pb-8">
					{renderGlyphs("Uppercase")}
					{renderGlyphs("Lowercase")}
					{renderGlyphs("Uppercase_Accented")}
					{renderGlyphs("Lowercase_Accented")}
					{renderGlyphs("Numbers")}
					{renderGlyphs("Superscript_And_Subscript")}
					{renderGlyphs("Fractions")}
					{renderGlyphs("Punctuation")}
					{renderGlyphs("Symbols")}
					{renderGlyphs("Mathematical")}
					{renderGlyphs("Ligatures")}
					{renderGlyphs("Accents")}
					{renderGlyphs("Alternative_Glyphs")}
					{renderGlyphs("Uncategorized")}
				</div>
			</div>

			{fonts.map((font: any, index: number) => {
				const url = font?.file?.asset?.url;
				const urlParts = url?.split("/");
				const filename = urlParts[urlParts.length - 1];
				const extension = filename?.split(".").pop();

				return (
					<Fragment key={`${slug}}-${slugify(font?.name)}-style-${index}`}>
						<style jsx global>{`
							@font-face {
								font-family: '${slugify(font?.name)}';
								src: url('/api/fonts/${filename}') format('${extension}');
								font-style: ${font?.style};
								font-display: block;
							}

							.character-tester-${slug}-${slugify(font?.name)} {
								font-family: '${slugify(font?.name)}' !important;
								font-style: ${font?.style} !important;
							}

							.character-tester-${slug}-${slugify(font?.name)}-glyph::before,
							.character-tester-${slug}-${slugify(font?.name)}-glyph::after {
								content: '';
								display: table;
							}
						`}</style>
					</Fragment>
				);
			})}

			{loadedFonts.map((font: any, index: number) => (
				<div
					key={`${slug}}-${slugify(font?.name)}-style-${index}`}
					dangerouslySetInnerHTML={{
						__html: `
					<style>
						.character-tester-${slug}-${slugify(font?.name)}-glyph::before {
							margin-bottom: ${font?.capsizeStyles?.["::before"]?.marginBottom};
						}

						.character-tester-${slug}-${slugify(font?.name)}-glyph::after {
							margin-top: ${font?.capsizeStyles?.["::after"]?.marginTop};
						}
					</style>
				`,
					}}
				/>
			))}
		</div>
	);
}
